import React, { useState } from "react";
import { T, fmt } from "./theme";
import { Modal, Input, Btn, Sel } from "./ui";
import { Icon } from "./Icons";
import { supabase } from "./supabase";
import { calcAwardVitalicio } from "./awards";
import { AWARDS, TEMPLATES_PROPOSTA } from "./data";
import { ClienteSelector, ModalNovoCliente, faltamCamposCliente } from "./ClienteSelector";

// % de comissão de venda direta pelo award vitalício do corretor
export function pctAwardDe(cor) {
  if (!cor) return null;
  const award = AWARDS.find(a => a.id === calcAwardVitalicio(cor).id);
  return award ? { pct: award.pctVendaDireta, nome: award.nome } : null;
}

// Aluguel: entrada ~50% + saldo com adicional de 15%/mês (semanas/4 = meses)
export function calcAluguel(projeto, entrada, semanas) {
  const p = paraNumero(projeto), e = paraNumero(entrada), s = parseInt(semanas, 10) || 0;
  const saldo = Math.max(0, p - e);
  const meses = s / 4;
  const adicional = +(saldo * 0.15 * meses).toFixed(2);
  const financiado = +(saldo + adicional).toFixed(2);
  const parcela = s > 0 ? +(financiado / s).toFixed(2) : 0;
  const totalContrato = +(e + financiado).toFixed(2);
  return { saldo, meses, adicional, financiado, parcela, totalContrato, semanas: s };
}

// Mini card de valor (resumo do aluguel)
function MiniInfo({ label, valor, cor, destaque }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: T.textSub, marginBottom: 2, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: destaque ? 15 : 13, fontWeight: 700, color: cor || T.text }}>{valor}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PLANTÃO — Modais compartilhados (corretor + admin)
// Ciclo do lead: atendido (valor + temperatura) → fechado (ficha) / perdido
// ═══════════════════════════════════════════════════════════════════════

export const TEMPERATURAS = [
  { id: "quente", label: "🔥 Quente" },
  { id: "morno", label: "🌡️ Morno" },
  { id: "frio", label: "❄️ Frio" },
];

export const MOTIVOS_PERDA = [
  "Preço", "Prazo de entrega", "Sumiu / parou de responder",
  "Comprou de concorrente", "Só pesquisando", "Outro",
];

// Tipos de móvel + campos extras por tipo. Comum a TODOS: modelo, quantidade, cor, observação.
export const MOVEL_TIPOS = [
  { id: "lavatorio",    label: "Lavatório",             campos: ["costura", "braco", "pe"] },
  { id: "poltrona_pro", label: "Poltrona Profissional", campos: ["costura", "tipo_base", "braco", "cabeca", "pe", "reclino"] },
  { id: "poltrona_esp", label: "Poltrona de Espera",    campos: ["costura", "braco", "pe"] },
  { id: "cirandinha",   label: "Cirandinha",            campos: ["costura"] },
  { id: "sofa",         label: "Sofá",                  campos: ["medida_l", "medida_a", "medida_c"] },
  { id: "espelho",      label: "Espelho",               campos: ["medida_a", "medida_l", "tipo_led"] },
  { id: "projetado_mdf", label: "Projetado MDF",        campos: ["medida_a", "medida_l", "medida_c"] },
];

const CAMPO_LABEL = {
  costura: "Detalhe da costura",
  braco: "Braço",
  pe: "Pé",
  tipo_base: "Tipo de base",
  cabeca: "Cabeça",
  reclino: "Reclino",
  medida_l: "Largura",
  medida_a: "Altura",
  medida_c: "Comprimento",
  tipo_led: "Tipo de LED",
  formato: "Formato (ex: retângulo, oval)",
};

export function movelNovo() {
  return { tipo: "lavatorio", modelo: "", quantidade: "1", cor: "", observacao: "", valor: "" };
}

export function paraNumero(v) {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return isNaN(n) ? 0 : n;
}

// Caixa alta no que for texto (não mexe em vazio). Email fica de fora (minúsculo).
function up(v) {
  return (v == null || v === "") ? v : String(v).toUpperCase();
}
// Uppercase nos campos de texto do móvel (não mexe em valor/quantidade)
function movelUpper(m) {
  const out = { ...m };
  ["modelo", "cor", "observacao", "costura", "braco", "pe", "tipo_base", "cabeca", "reclino", "tipo_led", "formato"].forEach(k => {
    if (out[k]) out[k] = up(out[k]);
  });
  return out;
}
// Uppercase nos dados do cliente (menos email)
function pedidoUpper(p) {
  const out = { ...p };
  ["nome", "sobrenome", "endereco", "ponto_referencia", "cpf_cnpj", "cidade", "estado"].forEach(k => {
    if (out[k]) out[k] = up(out[k]);
  });
  return out;
}
// Subtotal do móvel = valor unitário × quantidade
export function liquidoMovel(m) {
  return paraNumero(m.valor) * (parseInt(m.quantidade, 10) || 1);
}

// Ficha completa do móvel em texto (pra copiar / popup)
export function fichaMovelTexto(m) {
  const tipoDef = MOVEL_TIPOS.find(t => t.id === m.tipo);
  const linhas = [
    ["Tipo", tipoDef?.label],
    ["Modelo", m.modelo],
    ["Quantidade", m.quantidade],
    ["Cor", m.cor],
    ...(tipoDef?.campos || []).map(c => [CAMPO_LABEL[c] || c, m[c]]),
    ["Valor unitário", m.valor ? fmt.brl(paraNumero(m.valor)) : ""],
    ["Subtotal", fmt.brl(liquidoMovel(m))],
    ["Observação", m.observacao],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "");
  return linhas;
}

// Projeto inteiro em texto (cliente + móveis + financeiro) — pra copiar
export function projetoTexto(lead) {
  const pi = lead.pedido_info || {};
  const fin = lead.financeiro || {};
  const moveis = Array.isArray(lead.moveis) ? lead.moveis : [];
  const total = moveis.reduce((s, m) => s + liquidoMovel(m), 0);
  const L = [];
  L.push("INFORMAÇÕES DO CLIENTE");
  const nomeC = [pi.nome, pi.sobrenome].filter(Boolean).join(" ") || lead.nome_lead;
  if (nomeC) L.push("Nome: " + nomeC);
  if (pi.cpf_cnpj) L.push("CPF/CNPJ: " + pi.cpf_cnpj);
  if (pi.email) L.push("E-mail: " + pi.email);
  if (pi.telefone) L.push("Telefone: " + pi.telefone);
  if (pi.whatsapp || lead.telefone_lead) L.push("WhatsApp: " + (pi.whatsapp || lead.telefone_lead));
  if (pi.cep) L.push("CEP: " + pi.cep);
  if (pi.cidade) L.push("Cidade: " + pi.cidade);
  if (pi.estado) L.push("Estado: " + pi.estado);
  if (pi.endereco) L.push("Endereço: " + pi.endereco);
  if (pi.ponto_referencia) L.push("Ponto de referência: " + pi.ponto_referencia);
  L.push("");
  L.push("MÓVEIS");
  moveis.forEach((m, i) => {
    L.push(`— Móvel ${i + 1} —`);
    fichaMovelTexto(m).forEach(([k, v]) => L.push(`${k}: ${v}`));
    L.push("");
  });
  L.push("FINANCEIRO DA VENDA");
  L.push("Valor total: " + fmt.brl(total));
  if (fin.forma_pagamento) L.push("Pagamento: " + fin.forma_pagamento + (fin.parcelas ? ` (${fin.parcelas}x)` : ""));
  if (fin.pagamento_flex) L.push("Detalhe do pagamento: " + fin.pagamento_flex);
  L.push(fin.frete_gratis ? "Frete: GRÁTIS" : (fin.frete_cliente != null ? "Frete cliente: " + fmt.brl(fin.frete_cliente) : ""));
  if (fin.frete_cotacao != null && fin.frete_cotacao !== "") L.push("Cotação de frete: " + fmt.brl(fin.frete_cotacao));
  if (fin.transportadora) L.push("Transportadora: " + fin.transportadora);
  if (fin.cotacao_numero) L.push("Nº da cotação: " + fin.cotacao_numero);
  if (fin.entrega) L.push("Entrega: " + (fin.entrega === "pronta_entrega" ? "Pronta entrega" : "Fabricar"));
  if (lead.wix_numero) L.push("Pedido Nº: " + lead.wix_numero);
  return L.filter(x => x !== undefined && x !== null).join("\n");
}

// Modal de LEITURA do projeto (Ver projeto): tudo read-only + Copiar + Editar
export function ModalProjeto({ lead, onEditar, onClose }) {
  const [copiado, setCopiado] = useState(false);
  const pi = lead.pedido_info || {};
  const fin = lead.financeiro || {};
  const moveis = Array.isArray(lead.moveis) ? lead.moveis : [];
  const total = moveis.reduce((s, m) => s + liquidoMovel(m), 0);
  function copiar() {
    try {
      navigator.clipboard.writeText(projetoTexto(lead));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch (_) {}
  }
  const sec = { fontSize: 12, color: T.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, margin: "14px 0 6px" };
  const linha = (k, v) => (v || v === 0) ? <div style={{ fontSize: 12, color: T.text, marginBottom: 3 }}><span style={{ color: T.textSub }}>{k}:</span> {v}</div> : null;
  return (
    <Modal title="Projeto da venda" onClose={onClose} width={620}>
      <div style={{ maxHeight: "68vh", overflowY: "auto", paddingRight: 6 }}>
        <div style={{ ...sec, marginTop: 0 }}>Informações do Cliente</div>
        {linha("Nome", [pi.nome, pi.sobrenome].filter(Boolean).join(" ") || lead.nome_lead)}
        {linha("CPF/CNPJ", pi.cpf_cnpj)}
        {linha("E-mail", pi.email)}
        {linha("Telefone", pi.telefone)}
        {linha("WhatsApp", pi.whatsapp || lead.telefone_lead)}
        {linha("CEP", pi.cep)}
        {linha("Cidade", pi.cidade)}
        {linha("Estado", pi.estado)}
        {linha("Endereço", pi.endereco)}
        {linha("Ponto de referência", pi.ponto_referencia)}

        <div style={sec}>Móveis</div>
        {moveis.length === 0 && <div style={{ fontSize: 12, color: T.textDim }}>Nenhum móvel.</div>}
        {moveis.map((m, i) => (
          <div key={i} style={{ background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: 10, marginBottom: 8 }}>
            {fichaMovelTexto(m).map(([k, v], j) => (
              <div key={j} style={{ fontSize: 11.5, color: T.text, marginBottom: 2 }}><span style={{ color: T.textSub }}>{k}:</span> {v}</div>
            ))}
          </div>
        ))}

        <div style={sec}>Financeiro da Venda</div>
        {linha("Valor total", fmt.brl(total))}
        {linha("Pagamento", fin.forma_pagamento ? (fin.forma_pagamento + (fin.parcelas ? ` (${fin.parcelas}x)` : "")) : null)}
        {linha("Detalhe do pagamento", fin.pagamento_flex)}
        {linha("Frete", fin.frete_gratis ? "GRÁTIS" : (fin.frete_cliente != null && fin.frete_cliente !== "" ? fmt.brl(fin.frete_cliente) : null))}
        {linha("Cotação de frete", fin.frete_cotacao != null && fin.frete_cotacao !== "" ? fmt.brl(fin.frete_cotacao) : null)}
        {linha("Transportadora", fin.transportadora)}
        {linha("Nº da cotação", fin.cotacao_numero)}
        {linha("Entrega", fin.entrega ? (fin.entrega === "pronta_entrega" ? "Pronta entrega" : "Fabricar") : null)}
        {linha("Pedido Nº", lead.wix_numero)}
      </div>
      <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
        <Btn variant="ghost" onClick={onClose}>Fechar</Btn>
        {onEditar && <Btn variant="ghost" onClick={onEditar}>✏️ Editar projeto</Btn>}
        <Btn onClick={copiar}>{copiado ? "✓ Copiado!" : "📋 Copiar"}</Btn>
      </div>
    </Modal>
  );
}

// Cria o pedido no Wix + registra/atualiza a venda. corretorId = UUID do corretor.
// vendaId (opcional): consórcio quitando → atualiza a venda existente em vez de criar.
export async function criarPedidoWix(lead, ficha, corretorId, vendaId, opts = {}) {
  const itens = (ficha.moveis || []).map(m => ({
    nome: [MOVEL_TIPOS.find(t => t.id === m.tipo)?.label, m.modelo].filter(Boolean).join(" - ") || "Item",
    valor: paraNumero(m.valor),
    quantidade: parseInt(m.quantidade, 10) || 1,
  }));
  const pi = ficha.pedido_info || {};
  const resp = await fetch("/api/wix-create-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      itens: itens.length ? itens : undefined,
      valorTotal: parseFloat(ficha.financeiro?.valor_total) || 0,
      cliente: {
        nome: [pi.nome, pi.sobrenome].filter(Boolean).join(" ") || lead.nome_lead,
        email: pi.email,
        telefone: pi.whatsapp || pi.telefone || lead.telefone_lead,
        endereco: pi.endereco,
        cidade: pi.cidade,
        estado: pi.estado,
        cep: pi.cep,
        cpf: pi.cpf_cnpj,
      },
      corretorId: corretorId || null,
      vendaId: vendaId || null,
      modo: opts.modo || "quitar", // "confirmar" (venda normal) só grava nº; "quitar" libera comissão
      externalId: vendaId || lead.id, // = id da venda → webhook deduplica por channelInfo.externalOrderId
      origem: "plantao-" + (lead.campanha || "manual"),
    }),
  });
  return resp.json();
}

// Consórcio: cria a venda no fechamento SEM número Wix (tipo_venda=consorcio →
// cai em Comissões Agendadas). O admin ajusta a data prevista no financeiro da venda.
export async function criarVendaConsorcio(lead, ficha, corretorId) {
  const pi = ficha.pedido_info || {};
  const { data, error } = await supabase.from("vendas").insert({
    corretor_id: corretorId || null,
    valor: parseFloat(ficha.financeiro?.valor_total) || 0,
    status: "confirmada",
    cliente_nome: pi.nome || lead.nome_lead || null,
    cliente_telefone: pi.whatsapp || pi.telefone || lead.telefone_lead || null,
    tipo_venda: "consorcio",
    comissao_quitada: false,
    data_pedido: new Date().toISOString(),
    observacoes: "Consórcio (plantão) — aguardando quitação",
  }).select("id").single();
  if (error) throw error;
  return data;
}

// Cria a venda PENDENTE (sem número Wix), guardando a ficha na própria venda.
// O pedido Wix só é gerado quando o admin confirma (normal) ou quita (consórcio/aluguel).
// tipoFromForma: consorcio/aluguel → comissão diferida; resto → normal.
export async function criarVendaPendente(lead, ficha, corretorId) {
  const pi = ficha.pedido_info || {};
  const fim = ficha.financeiro || {};
  const forma = fim.forma_pagamento || "cartao";
  // tipo_venda = forma de pagamento (cartao | pix | flexivel | consorcio | aluguel).
  // Diferida (comissão só na quitação) = consorcio | aluguel.
  const tipo = forma;
  const diferida = tipo === "consorcio" || tipo === "aluguel";
  const cid = corretorId || ficha.corretor_id || null;
  const nome = [pi.nome, pi.sobrenome].filter(Boolean).join(" ") || lead.nome_lead || null;
  const row = {
    corretor_id: cid,
    valor: parseFloat(fim.valor_total) || 0,
    status: "pendente",
    cliente_nome: nome,
    cliente_telefone: pi.whatsapp || pi.telefone || lead.telefone_lead || null,
    tipo_venda: tipo,
    comissao_quitada: diferida ? false : true, // só consórcio/aluguel são diferidos
    pedido_info: pi,
    moveis: ficha.moveis || [],
    financeiro: fim,
    data_pedido: new Date().toISOString(),
    observacoes: "Aguardando confirmação do admin" + (diferida ? " · " + tipo : ""),
  };
  // Comissão: se o fechamento já trouxe (bloco admin), grava o snapshot
  if (ficha.comissao_pct != null) row.comissao_pct = ficha.comissao_pct;
  if (ficha.comissao_valor != null) row.comissao_valor = ficha.comissao_valor;
  const { data, error } = await supabase.from("vendas").insert(row).select("id").single();
  if (error) throw error;
  return data;
}

// Upsert da comissão direta (nivel 0) na tabela comissoes, por venda_id.
// Usa o snapshot da venda (comissao_pct/valor) se existir; senão calcula pelo award.
// Defensivo: se já existe linha pra venda, atualiza (não duplica).
export async function upsertComissaoVenda(venda, corretor) {
  if (!venda || !venda.corretor_id) return;
  const bruto = parseFloat(venda.valor) || 0;
  // Frete comissionável = cotação de frete (fechamento); fallback no frete antigo
  const frete = parseFloat(venda.financeiro && venda.financeiro.frete_cotacao) || parseFloat(venda.frete) || 0;
  const taxa = parseFloat(venda.taxa_cartao) || 0;
  const base = Math.max(0, bruto - frete - taxa);
  let pct = venda.comissao_pct != null ? parseFloat(venda.comissao_pct) : null;
  let valor = venda.comissao_valor != null ? parseFloat(venda.comissao_valor) : null;
  if (pct == null && corretor) {
    const info = pctAwardDe(corretor);
    if (info) { pct = info.pct; valor = +(base * pct / 100).toFixed(2); }
  }
  if (pct == null) return;
  if (valor == null) valor = +(base * pct / 100).toFixed(2);
  // Ciclo/mês da comissão: usa o escolhido no financeiro da venda, senão o mês atual
  const mesRef = (venda.financeiro && venda.financeiro.ciclo_mes)
    || (venda.data_pedido ? String(venda.data_pedido).slice(0, 7) : new Date().toISOString().slice(0, 7));
  const row = {
    corretor_id: venda.corretor_id,
    venda_id: venda.id,
    nivel: 0,
    comissao_pct: pct, percentual: pct,
    comissao_valor: valor, valor: valor, saldo_aberto: valor,
    valor_comissionavel: base, valor_venda: bruto,
    cliente_nome: venda.cliente_nome || null,
    mes_ref: mesRef,
    observacoes: "Comissão automática (confirmação da venda)",
  };
  const { data: existe } = await supabase.from("comissoes").select("id").eq("venda_id", venda.id).eq("nivel", 0).limit(1);
  if (existe && existe.length) {
    await supabase.from("comissoes").update(row).eq("id", existe[0].id);
  } else {
    await supabase.from("comissoes").insert({ ...row, criado_em: new Date().toISOString() });
  }
}

// ─── Modal: marcar atendido (valor da proposta + temperatura) ─────────
export function ModalAtendido({ lead, onSalvar, onCancelar }) {
  const [valor, setValor] = useState(lead.valor_proposta ?? "");
  const [temp, setTemp] = useState(lead.temperatura || "morno");
  return (
    <Modal title={`Atendido · ${lead.nome_lead || "Cliente"}`} onClose={onCancelar} width={440}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
        Você conversou com o cliente. Informe o valor da proposta (mesmo sem fechar) e o quão quente ele está.
      </div>
      <Input label="Valor da proposta (R$)" type="number" value={valor} onChange={e => setValor(e.target.value)} placeholder="Ex: 8500" />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Temperatura do lead</div>
        <Sel value={temp} onChange={e => setTemp(e.target.value)}
          style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
          {TEMPERATURAS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </Sel>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={() => onSalvar(lead.id, { valor_proposta: valor, temperatura: temp })} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}

// ─── Modal: marcar perdido (motivo) ───────────────────────────────────
export function ModalPerdido({ lead, onSalvar, onCancelar }) {
  const [motivo, setMotivo] = useState("Preço");
  return (
    <Modal title={`Perdido · ${lead.nome_lead || "Cliente"}`} onClose={onCancelar} width={420}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
        Registrar por que esse lead não fechou ajuda a gente a melhorar. Sem culpa — faz parte.
      </div>
      <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Motivo da perda</div>
      <Sel value={motivo} onChange={e => setMotivo(e.target.value)}
        style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
        {MOTIVOS_PERDA.map(m => <option key={m} value={m}>{m}</option>)}
      </Sel>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn variant="danger" onClick={() => onSalvar(lead.id, { motivo })}>Marcar perdido</Btn>
      </div>
    </Modal>
  );
}

// ─── Modal: fechamento (ficha de fabricação + financeiro) ─────────────
export function ModalFechamento({ lead, onSalvar, onCancelar, corretores = [], isAdmin = false, currentUser, modoTitulo, scopeCorretorId, modoProposta = false }) {
  const pi0 = lead.pedido_info || {};
  const [pedido, setPedido] = useState({
    nome: pi0.nome || lead.nome_lead || "",
    sobrenome: pi0.sobrenome || "",
    endereco: pi0.endereco || "",
    cidade: pi0.cidade || "",
    estado: pi0.estado || "",
    cep: pi0.cep || "",
    ponto_referencia: pi0.ponto_referencia || "",
    cpf_cnpj: pi0.cpf_cnpj || "",
    email: pi0.email || "",
    telefone: pi0.telefone || "",
    whatsapp: pi0.whatsapp || lead.telefone_lead || "",
  });
  const moveisIniciais = Array.isArray(lead.moveis) && lead.moveis.length ? lead.moveis : [];
  const [moveis, setMoveis] = useState(moveisIniciais);
  const [draft, setDraft] = useState(moveisIniciais.length ? null : movelNovo());
  const [editIndex, setEditIndex] = useState(-1);

  const totalDe = (lista) => lista.reduce((s, m) => s + liquidoMovel(m), 0);
  const listaLive = (draft && (draft.modelo || "").trim())
    ? (editIndex >= 0 ? moveis.map((m, i) => i === editIndex ? draft : m) : [...moveis, draft])
    : moveis;
  const valorTotalMoveis = totalDe(listaLive);

  const f0 = lead.financeiro || {};
  const [fin, setFin] = useState({
    valor_total: f0.valor_total ?? lead.valor_proposta ?? "",
    forma_pagamento: f0.forma_pagamento || "cartao",
    parcelas: f0.parcelas ?? "",
    frete_cliente: f0.frete_cliente ?? "",
    frete_gratis: f0.frete_gratis ?? false,
    frete_cotacao: f0.frete_cotacao ?? "",
    cotacao_numero: f0.cotacao_numero || "",
    transportadora: f0.transportadora || "",
    entrega: f0.entrega || "fabricar",
    pagamento_flex: f0.pagamento_flex || "",
  });

  // Template da proposta (goprospero) — usado no fechamento e na proposta
  const [template, setTemplate] = useState(f0.template || "");

  // Valor total = móveis + frete pago pelo cliente
  const freteCli = fin.frete_gratis ? 0 : paraNumero(fin.frete_cliente);
  const valorTotal = valorTotalMoveis + freteCli;

  // Aluguel: entrada + prazo (semanas) → parcela semanal
  const aluguel = fin.forma_pagamento === "aluguel"
    ? calcAluguel(valorTotal, fin.valor_entrada, fin.prazo_semanas) : null;

  // Bloco admin: corretor + comissão automática por award (editável)
  const [corretorId, setCorretorId] = useState(lead.corretor_id || "");
  const [editandoComissao, setEditandoComissao] = useState(false);
  const [comManual, setComManual] = useState({ pct: "", valor: "" });
  const corSel = corretorId ? corretores.find(c => c._uuid === corretorId) : null;
  const awardInfo = pctAwardDe(corSel);
  const baseComissao = valorTotalMoveis; // líquido comissionável (móveis, sem frete)
  const comAutoPct = awardInfo ? awardInfo.pct : 0;
  const comAutoValor = +(baseComissao * comAutoPct / 100).toFixed(2);
  const comPct = editandoComissao && comManual.pct !== "" ? parseFloat(comManual.pct) : comAutoPct;
  const comValor = editandoComissao && comManual.valor !== "" ? parseFloat(comManual.valor) : comAutoValor;
  function editarComPct(v) {
    const pct = v === "" ? "" : parseFloat(v);
    const val = pct === "" ? "" : String(+(baseComissao * pct / 100).toFixed(2));
    setComManual({ pct: v, valor: val });
  }
  function editarComValor(v) {
    const val = v === "" ? "" : parseFloat(v);
    const pct = (baseComissao > 0 && val !== "") ? String(+((val / baseComissao) * 100).toFixed(2)) : "";
    setComManual({ pct, valor: v });
  }

  const [clienteSelId, setClienteSelId] = useState(pi0.cliente_id || null);
  const [modalCadastro, setModalCadastro] = useState(false);
  function setPedidoCampo(k, v) { setPedido(p => ({ ...p, [k]: v })); }
  // Preenche os dados do cliente ao selecionar um cliente existente
  function selecionarCliente(cli) {
    setClienteSelId(cli.id || null);
    setPedido(p => ({
      ...p,
      nome: cli.nome || "", sobrenome: cli.sobrenome || "",
      cpf_cnpj: cli.cpf_cnpj || "", email: cli.email || "",
      telefone: cli.telefone || "", whatsapp: cli.whatsapp || p.whatsapp || "",
      cep: cli.cep || "", endereco: cli.endereco || "",
      cidade: cli.cidade || "", estado: cli.estado || "",
      ponto_referencia: cli.ponto_referencia || "",
      cliente_id: cli.id || null,
    }));
  }
  const temCliente = !!(pedido.nome || pedido.cpf_cnpj);
  const faltamCli = faltamCamposCliente(pedido);
  const cadastroCompleto = faltamCli.length === 0;
  function limparCliente() {
    setClienteSelId(null);
    setPedido(p => ({ ...p, nome: "", sobrenome: "", cpf_cnpj: "", email: "", telefone: "", whatsapp: "", cep: "", endereco: "", cidade: "", estado: "", ponto_referencia: "", cliente_id: null }));
  }
  function abrirNovoMovel() { setDraft(movelNovo()); setEditIndex(-1); }
  function editarMovel(i) { setDraft({ ...moveis[i] }); setEditIndex(i); }
  function removerMovel(i) {
    setMoveis(ms => ms.filter((_, idx) => idx !== i));
    if (editIndex === i) { setDraft(null); setEditIndex(-1); }
  }
  function concluirDraft() {
    if (!draft) return;
    const falta = [];
    if (!(draft.modelo || "").trim()) falta.push("Modelo");
    if (!(draft.cor || "").trim()) falta.push("Cor");
    if (!(parseInt(draft.quantidade, 10) > 0)) falta.push("Quantidade");
    if (!(paraNumero(draft.valor) > 0)) falta.push("Valor unitário");
    if (falta.length) { alert("Preencha no móvel: " + falta.join(", ")); return; }
    setMoveis(ms => editIndex >= 0 ? ms.map((m, idx) => idx === editIndex ? draft : m) : [...ms, draft]);
    setDraft(null); setEditIndex(-1);
  }
  function cancelarDraft() { setDraft(null); setEditIndex(-1); }

  const mostraParcelas = fin.forma_pagamento === "cartao" || fin.forma_pagamento === "consorcio";

  function salvar() {
    // 1) Cliente obrigatório. Proposta pede menos (nome, sobrenome, whatsapp, email, cep).
    if (modoProposta) {
      const faltamP = [];
      if (!(pedido.nome || "").trim()) faltamP.push("Nome");
      if (!(pedido.sobrenome || "").trim()) faltamP.push("Sobrenome");
      if (!(pedido.whatsapp || "").trim() && !(pedido.telefone || "").trim()) faltamP.push("WhatsApp");
      if (!(pedido.email || "").trim()) faltamP.push("E-mail");
      if (!(pedido.cep || "").trim()) faltamP.push("CEP");
      if (faltamP.length) { alert("Pra criar a proposta, preencha: " + faltamP.join(", ")); return; }
    } else {
      const faltamC = faltamCamposCliente(pedido);
      if (faltamC.length) { alert("Complete o cadastro do cliente antes de fechar.\n\nFalta: " + faltamC.join(", ")); return; }
    }

    let finalMoveis = moveis;
    if (draft && ((draft.modelo || "").trim() || paraNumero(draft.valor) > 0)) {
      finalMoveis = editIndex >= 0 ? moveis.map((m, i) => i === editIndex ? draft : m) : [...moveis, draft];
    }
    // 2) Pelo menos 1 móvel, com modelo/cor/quantidade/valor
    if (!finalMoveis.length) { alert("Adicione pelo menos um móvel."); return; }
    const invalido = finalMoveis.find(m => !(m.modelo || "").trim() || !(m.cor || "").trim() || !(parseInt(m.quantidade, 10) > 0) || !(paraNumero(m.valor) > 0));
    if (invalido) { alert("Cada móvel precisa de: Modelo, Cor, Quantidade e Valor unitário (maior que zero)."); return; }

    const freteFinal = fin.frete_gratis ? 0 : paraNumero(fin.frete_cliente);
    const totalFinal = totalDe(finalMoveis) + freteFinal;
    const alu = fin.forma_pagamento === "aluguel"
      ? calcAluguel(totalFinal, fin.valor_entrada, fin.prazo_semanas) : null;
    onSalvar(lead.id, {
      pedido_info: pedidoUpper(pedido),
      moveis: finalMoveis.map(movelUpper),
      financeiro: {
        ...fin,
        valor_total: totalFinal,
        parcelas: fin.parcelas === "" ? null : parseInt(fin.parcelas, 10),
        frete_cliente: fin.frete_gratis ? 0 : (fin.frete_cliente === "" ? null : parseFloat(fin.frete_cliente)),
        frete_cotacao: fin.frete_cotacao === "" ? null : parseFloat(fin.frete_cotacao),
        // Aluguel: guarda entrada/prazo/parcela/adicional pra fatura Wix + acompanhamento
        valor_entrada: fin.forma_pagamento === "aluguel" ? (fin.valor_entrada === "" ? null : parseFloat(fin.valor_entrada)) : null,
        prazo_semanas: fin.forma_pagamento === "aluguel" ? (parseInt(fin.prazo_semanas, 10) || null) : null,
        aluguel_parcela: alu ? alu.parcela : null,
        aluguel_adicional: alu ? alu.adicional : null,
        aluguel_financiado: alu ? alu.financiado : null,
        aluguel_total_contrato: alu ? alu.totalContrato : null,
        template: template || null,
      },
      template: template || null,
      // Corretor + comissão (bloco admin; no plantão do corretor o callback ignora e usa o dono)
      corretor_id: corretorId || null,
      comissao_pct: corretorId ? comPct : null,
      comissao_valor: corretorId ? comValor : null,
      base_comissionavel: valorTotalMoveis,
    });
  }

  const secTitle = { fontSize: 12, color: T.gold, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", margin: "4px 0 8px" };

  return (
    <Modal title={modoTitulo || "Fechamento"} onClose={onCancelar} width={720}>
      <style>{`.ficha-caps input:not([type="number"]) { text-transform: uppercase; } .ficha-caps label { text-transform: none !important; }`}</style>
      <div className="ficha-caps" style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 6, display: "flex", flexDirection: "column", gap: 6 }}>

        {/* Dados do cliente — seletor + resumo (cadastro completo é popup separado) */}
        <div style={secTitle}>Informações do Cliente</div>
        {temCliente ? (
          <div style={{ background: T.card, border: `1px solid ${cadastroCompleto ? T.border : T.warn + "88"}`, borderRadius: T.r.md, padding: "12px 14px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{[pedido.nome, pedido.sobrenome].filter(Boolean).join(" ") || "—"}</div>
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                  {[pedido.cpf_cnpj, (pedido.cidade && `${pedido.cidade}-${pedido.estado || ""}`), (pedido.whatsapp || pedido.telefone)].filter(Boolean).join(" · ") || "sem dados"}
                </div>
                {pedido.endereco && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>📍 {pedido.endereco}{pedido.cep ? ` · ${pedido.cep}` : ""}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button onClick={() => setModalCadastro(true)} style={{ background: cadastroCompleto ? "none" : T.warn + "22", border: `1px solid ${cadastroCompleto ? T.border : T.warn}`, color: cadastroCompleto ? T.textSec : T.warn, cursor: "pointer", fontSize: 11, fontFamily: "inherit", padding: "6px 10px", borderRadius: T.r.md, whiteSpace: "nowrap", fontWeight: 600 }}>
                  {cadastroCompleto ? "✏️ Editar cadastro" : "⚠️ Completar cadastro"}
                </button>
                <button onClick={limparCliente} style={{ background: "none", border: `1px solid ${T.border}`, color: T.textSec, cursor: "pointer", fontSize: 11, fontFamily: "inherit", padding: "6px 10px", borderRadius: T.r.md, whiteSpace: "nowrap" }}>✕ Trocar</button>
              </div>
            </div>
            {!cadastroCompleto && (
              <div style={{ fontSize: 10, color: T.warn, marginTop: 8, fontWeight: 600 }}>Cadastro incompleto — falta: {faltamCli.join(", ")}</div>
            )}
          </div>
        ) : (
          <div style={{ marginBottom: 4 }}>
            <ClienteSelector corretorId={scopeCorretorId || null} onSelect={selecionarCliente} />
            <div style={{ fontSize: 9, color: T.textDim, marginTop: 3 }}>Busque um cliente existente ou clique em "Adicionar novo cliente".</div>
          </div>
        )}
        {modalCadastro && (
          <ModalNovoCliente
            titulo={cadastroCompleto ? "Editar cadastro do cliente" : "Completar cadastro do cliente"}
            corretorId={scopeCorretorId || null}
            clienteId={clienteSelId}
            inicial={pedido}
            onSalvar={(cli) => { selecionarCliente(cli); setModalCadastro(false); }}
            onCancelar={() => setModalCadastro(false)}
          />
        )}

        {/* Template da proposta — oculto por enquanto (escolhido direto no Wix Proposals) */}

        {/* Móveis */}
        <div style={{ ...secTitle, marginTop: 14 }}>Móveis</div>
        {moveis.map((m, i) => (
          <MovelRow key={i} movel={m} onEditar={() => editarMovel(i)} onRemover={() => removerMovel(i)} />
        ))}
        {draft ? (
          <div style={{ background: T.input, border: `1px solid ${T.brand}55`, borderRadius: T.r.md, padding: 12 }}>
            <MovelItem movel={draft} onChange={patch => setDraft(d => ({ ...d, ...patch }))} />
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <Btn onClick={concluirDraft} style={{ fontSize: 12 }} icon={<Icon.check size={12} />}>Adicionar móvel</Btn>
              {(moveis.length > 0 || editIndex >= 0) && (
                <Btn variant="ghost" onClick={cancelarDraft} style={{ fontSize: 12 }}>Cancelar</Btn>
              )}
            </div>
          </div>
        ) : (
          <Btn variant="ghost" onClick={abrirNovoMovel} style={{ fontSize: 12, alignSelf: "flex-start" }}>+ Adicionar móvel</Btn>
        )}

        {/* Financeiro */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Valor total da venda</div>
            <div style={{ background: T.card, border: `1px solid ${T.gold}44`, borderRadius: T.r.md, padding: "10px 12px", fontSize: 14, fontWeight: 700, color: T.gold, boxSizing: "border-box" }}>{fmt.brl(valorTotal)}</div>
            <div style={{ fontSize: 9, color: T.textDim, marginTop: 3 }}>Móveis + frete do cliente</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Forma de pagamento</div>
            <Sel value={fin.forma_pagamento} onChange={e => setFin({ ...fin, forma_pagamento: e.target.value })}
              style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
              <option value="cartao">💳 Cartão</option>
              <option value="pix">⚡ Pix</option>
              <option value="consorcio">📅 Consórcio</option>
              <option value="aluguel">🏠 Aluguel</option>
              <option value="flexivel">🔀 Flex</option>
            </Sel>
          </div>
          {mostraParcelas && (
            <Input label="Parcelas (quantas vezes)" type="number" value={fin.parcelas} onChange={e => setFin({ ...fin, parcelas: e.target.value })} />
          )}
          {fin.forma_pagamento === "flexivel" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <Input label="Como será o pagamento (personalizado)" value={fin.pagamento_flex} onChange={e => setFin({ ...fin, pagamento_flex: e.target.value })} />
            </div>
          )}
          {fin.forma_pagamento === "aluguel" && (
            <div style={{ gridColumn: "1 / -1", background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: 12, marginTop: 2 }}>
              <div style={{ fontSize: 11, color: T.gold, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>🏠 Plano de aluguel</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Input label="Valor da entrada (R$)" type="text" inputMode="decimal" value={fin.valor_entrada || ""} onChange={e => setFin({ ...fin, valor_entrada: e.target.value })} />
                <Input label="Prazo (nº de semanas)" type="number" value={fin.prazo_semanas || ""} onChange={e => setFin({ ...fin, prazo_semanas: e.target.value })} />
              </div>
              {aluguel && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${T.border}` }}>
                  <MiniInfo label="Saldo pendente" valor={fmt.brl(aluguel.saldo)} />
                  <MiniInfo label={`Adicional (15%/mês · ${aluguel.meses.toFixed(1)} m)`} valor={fmt.brl(aluguel.adicional)} cor={T.warn} />
                  <MiniInfo label="Total financiado" valor={fmt.brl(aluguel.financiado)} />
                  <MiniInfo label="Parcela semanal" valor={fmt.brl(aluguel.parcela)} cor={T.gold} destaque />
                  <MiniInfo label="Total do contrato" valor={fmt.brl(aluguel.totalContrato)} />
                </div>
              )}
              <div style={{ fontSize: 9, color: T.textDim, marginTop: 6 }}>Entrada + parcelas cobrem o total. Comissão só na quitação. Juros/atraso são controlados na fatura Wix.</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Entrega</div>
            <Sel value={fin.entrega} onChange={e => setFin({ ...fin, entrega: e.target.value })}
              style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
              <option value="fabricar">🏭 Fabricar</option>
              <option value="pronta_entrega">📦 Pronta entrega</option>
            </Sel>
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, cursor: "pointer", fontSize: 12, color: T.text }}>
          <input type="checkbox" checked={fin.frete_gratis} onChange={e => setFin({ ...fin, frete_gratis: e.target.checked })} style={{ accentColor: T.brand, cursor: "pointer" }} />
          Frete grátis para o cliente
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
          {!fin.frete_gratis && (
            <Input label="Frete pago pelo cliente (R$)" type="text" inputMode="decimal" value={fin.frete_cliente} onChange={e => setFin({ ...fin, frete_cliente: e.target.value })} />
          )}
          <Input label="Cotação de frete (R$)" type="text" inputMode="decimal" value={fin.frete_cotacao} onChange={e => setFin({ ...fin, frete_cotacao: e.target.value })} />
          <Input label="Nº da cotação" value={fin.cotacao_numero} onChange={e => setFin({ ...fin, cotacao_numero: e.target.value })} />
          <Input label="Transportadora" value={fin.transportadora} onChange={e => setFin({ ...fin, transportadora: e.target.value })} />
        </div>

        {/* Bloco admin: corretor + comissão automática por award (só no login de admin) */}
        {isAdmin && (
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.md, padding: 12, marginTop: 12 }}>
            <div style={{ fontSize: 11, color: T.gold, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>Corretor(a)</div>
            <Sel value={corretorId} onChange={e => { setCorretorId(e.target.value); setEditandoComissao(false); setComManual({ pct: "", valor: "" }); }}
              style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
              <option value="">— Sem corretor —</option>
              {corretores.filter(c => !c.banido).map(c => (
                <option key={c._uuid || c.id} value={c._uuid}>{c.nome}{c.cidade ? ` · ${c.cidade}-${c.uf || ""}` : ""}</option>
              ))}
            </Sel>
            {corretorId && (
              <div style={{ marginTop: 10 }}>
                {!editandoComissao ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 12, color: T.text }}>
                      Comissão automática: <b style={{ color: T.gold }}>{comAutoPct}%</b> = <b style={{ color: T.gold }}>{fmt.brl(comAutoValor)}</b>
                      {awardInfo && <span style={{ color: T.textDim }}> · nível {awardInfo.nome}</span>}
                    </div>
                    <Btn variant="ghost" onClick={() => setEditandoComissao(true)} style={{ fontSize: 11 }}>✏️ Editar comissão</Btn>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <Input label="% Comissão" type="number" value={comManual.pct} onChange={e => editarComPct(e.target.value)} placeholder={String(comAutoPct)} />
                      <Input label="Valor comissão (R$)" type="number" value={comManual.valor} onChange={e => editarComValor(e.target.value)} placeholder={String(comAutoValor)} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                      <span style={{ fontSize: 10, color: T.textDim }}>Base: {fmt.brl(baseComissao)} (móveis, sem frete). Editar % recalcula valor e vice-versa.</span>
                      <button onClick={() => { setEditandoComissao(false); setComManual({ pct: "", valor: "" }); }} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 10, textDecoration: "underline" }}>voltar ao automático</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
        <Btn variant="ghost" onClick={onCancelar}>Cancelar</Btn>
        <Btn onClick={salvar} icon={<Icon.check size={14} />}>{modoProposta ? "📄 Criar proposta" : "✅ Confirmar fechamento"}</Btn>
      </div>
    </Modal>
  );
}

// ─── Linha compacta de um móvel já concluído ──────────────────────────
function MovelRow({ movel, onEditar, onRemover }) {
  const tipoDef = MOVEL_TIPOS.find(t => t.id === movel.tipo);
  const qtd = parseInt(movel.quantidade, 10) || 1;
  const partes = [
    tipoDef?.label,
    movel.modelo,
    `Qtd ${qtd}`,
    movel.cor ? `Cor ${movel.cor}` : null,
    movel.valor ? fmt.brl(liquidoMovel(movel)) : null,
  ].filter(Boolean);
  const linkBtn = { background: "none", border: "none", cursor: "pointer", fontSize: 11, textDecoration: "underline", fontFamily: "inherit" };
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap",
      padding: "8px 12px", background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${T.ok}`, borderRadius: T.r.md,
    }}>
      <div style={{ fontSize: 12, color: T.text, minWidth: 0 }}>🛋️ <b>{partes.join(" · ")}</b></div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={onEditar} style={{ ...linkBtn, color: T.brand }}>✏️ Editar</button>
        <button onClick={onRemover} style={{ ...linkBtn, color: T.err }}>🗑️ Remover</button>
      </div>
    </div>
  );
}

// ─── Formulário de móvel (campos dinâmicos por tipo) ──────────────────
function MovelItem({ movel, onChange }) {
  const tipoDef = MOVEL_TIPOS.find(t => t.id === movel.tipo) || MOVEL_TIPOS[0];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <div>
        <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Tipo</div>
        <Sel value={movel.tipo} onChange={e => onChange({ tipo: e.target.value })}
          style={{ width: "100%", fontSize: 13, padding: "10px 12px", boxSizing: "border-box", background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.md, color: T.text }}>
          {MOVEL_TIPOS.map(t => <option key={t.id} value={t.id}>{t.label.toUpperCase()}</option>)}
        </Sel>
      </div>
      <Input label="Modelo" value={movel.modelo || ""} onChange={e => onChange({ modelo: e.target.value })} />
      <Input label="Cor" value={movel.cor || ""} onChange={e => onChange({ cor: e.target.value })} />
      {tipoDef.campos.map(campo => (
        <Input
          key={campo}
          label={CAMPO_LABEL[campo] || campo}
          value={movel[campo] || ""}
          onChange={e => onChange({ [campo]: e.target.value })}
        />
      ))}
      <Input label="Quantidade" type="number" value={movel.quantidade || ""} onChange={e => onChange({ quantidade: e.target.value })} />
      <Input label="Valor unitário (R$)" type="text" inputMode="decimal" value={movel.valor || ""} onChange={e => onChange({ valor: e.target.value })} />
      <div style={{ gridColumn: "1 / -1" }}>
        <div style={{ fontSize: 10, color: T.textSub, marginBottom: 4, fontWeight: 600, letterSpacing: 0.5 }}>Subtotal (qtd × unitário)</div>
        <div style={{ padding: "10px 12px", background: T.card, border: `1px solid ${T.border}`, borderRadius: T.r.md, fontSize: 14, fontWeight: 700, color: T.gold, boxSizing: "border-box" }}>{fmt.brl(liquidoMovel(movel))}</div>
      </div>
      <div style={{ gridColumn: "1 / -1" }}>
        <Input label="Observação" value={movel.observacao || ""} onChange={e => onChange({ observacao: e.target.value })} />
      </div>
    </div>
  );
}
